import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { AlertTriangle, CreditCard, ExternalLink, Loader2, LogOut } from 'lucide-react';

const BillingRequiredPage = () => {
  const { api, logout, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleUpdateBilling = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Try billing portal first (for existing customers)
      const portalResponse = await api.post('/billing/subscription/billing-portal', {
        origin_url: window.location.origin
      });
      
      if (portalResponse.data.portal_url) {
        window.location.href = portalResponse.data.portal_url;
        return;
      }
    } catch (portalError) {
      // If billing portal fails, try checkout session
      try {
        const checkoutResponse = await api.post('/billing/subscription/checkout-session', {
          origin_url: window.location.origin
        });
        
        if (checkoutResponse.data.checkout_url) {
          window.location.href = checkoutResponse.data.checkout_url;
          return;
        }
      } catch (checkoutError) {
        setError('Unable to open billing page. Please contact support.');
      }
    }
    
    setLoading(false);
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <img 
              src="/ors-logo.svg" 
              alt="Onsite Rail Solutions" 
              className="h-12 w-auto"
              srcSet="/ors-logo.svg 1x, /ors-logo@2x.png 2x"
            />
          </div>
          
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-600" />
          </div>
          
          <CardTitle className="text-xl font-bold text-[#323033]">
            Billing Required
          </CardTitle>
          <CardDescription className="text-[#5A5A5A] mt-2">
            Your trial has ended or your subscription payment is overdue. 
            To continue using Onsite Rail Solutions, please update your billing information.
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
          
          <Button
            onClick={handleUpdateBilling}
            disabled={loading}
            className="w-full bg-[#005EF8] hover:bg-[#0047C2] text-white h-12"
            data-testid="update-billing-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Opening Billing...
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Update Billing Information
                <ExternalLink className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
          
          <div className="text-center text-sm text-[#808080]">
            <p>You'll be redirected to our secure payment portal.</p>
          </div>
          
          <hr className="my-4" />
          
          <Button
            onClick={handleLogout}
            variant="outline"
            className="w-full"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
          
          <p className="text-xs text-center text-[#808080]">
            Need help? Contact us at{' '}
            <a 
              href="mailto:support@onsiterailsolutions.com" 
              className="text-[#005EF8] hover:underline"
            >
              support@onsiterailsolutions.com
            </a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingRequiredPage;
